// The 54 parshiyot of the Chumash, grouped by book, with their canonical
// STANDALONE refs (the text picker lets you open any of them). Refs were resolved
// from Sefaria (api/calendars/next-read/<name>) and pinned here so the picker is
// deterministic and works offline (PWA). The doubled pairs (Chukat-Balak,
// Matot-Masei, Nitzavim-Vayelech) and the final three (which next-read could not
// return standalone) use the canonical standalone boundaries, verified by
// contiguity against the combined ranges Sefaria returned.
//
// `ref` feeds getParashaVerses() directly; `en`/`he` are display-only.

export interface Parasha {
  en: string;
  he: string;
  ref: string;
}

export interface ParashaBook {
  book: string;
  bookHe: string;
  parshiyot: Parasha[];
}

export const PARSHIYOT: ParashaBook[] = [
  {
    book: "Bereshit",
    bookHe: "בראשית",
    parshiyot: [
      { en: "Bereshit", he: "בראשית", ref: "Genesis 1:1-6:8" },
      { en: "Noach", he: "נח", ref: "Genesis 6:9-11:32" },
      { en: "Lech-Lecha", he: "לך לך", ref: "Genesis 12:1-17:27" },
      { en: "Vayera", he: "וירא", ref: "Genesis 18:1-22:24" },
      { en: "Chayei Sara", he: "חיי שרה", ref: "Genesis 23:1-25:18" },
      { en: "Toldot", he: "תולדות", ref: "Genesis 25:19-28:9" },
      { en: "Vayetzei", he: "ויצא", ref: "Genesis 28:10-32:3" },
      { en: "Vayishlach", he: "וישלח", ref: "Genesis 32:4-36:43" },
      { en: "Vayeshev", he: "וישב", ref: "Genesis 37:1-40:23" },
      { en: "Miketz", he: "מקץ", ref: "Genesis 41:1-44:17" },
      { en: "Vayigash", he: "ויגש", ref: "Genesis 44:18-47:27" },
      { en: "Vayechi", he: "ויחי", ref: "Genesis 47:28-50:26" },
    ],
  },
  {
    book: "Shemot",
    bookHe: "שמות",
    parshiyot: [
      { en: "Shemot", he: "שמות", ref: "Exodus 1:1-6:1" },
      { en: "Vaera", he: "וארא", ref: "Exodus 6:2-9:35" },
      { en: "Bo", he: "בא", ref: "Exodus 10:1-13:16" },
      { en: "Beshalach", he: "בשלח", ref: "Exodus 13:17-17:16" },
      { en: "Yitro", he: "יתרו", ref: "Exodus 18:1-20:23" },
      { en: "Mishpatim", he: "משפטים", ref: "Exodus 21:1-24:18" },
      { en: "Terumah", he: "תרומה", ref: "Exodus 25:1-27:19" },
      { en: "Tetzaveh", he: "תצוה", ref: "Exodus 27:20-30:10" },
      { en: "Ki Tisa", he: "כי תשא", ref: "Exodus 30:11-34:35" },
      { en: "Vayakhel", he: "ויקהל", ref: "Exodus 35:1-38:20" },
      { en: "Pekudei", he: "פקודי", ref: "Exodus 38:21-40:38" },
    ],
  },
  {
    book: "Vayikra",
    bookHe: "ויקרא",
    parshiyot: [
      { en: "Vayikra", he: "ויקרא", ref: "Leviticus 1:1-5:26" },
      { en: "Tzav", he: "צו", ref: "Leviticus 6:1-8:36" },
      { en: "Shmini", he: "שמיני", ref: "Leviticus 9:1-11:47" },
      { en: "Tazria", he: "תזריע", ref: "Leviticus 12:1-13:59" },
      { en: "Metzora", he: "מצורע", ref: "Leviticus 14:1-15:33" },
      { en: "Achrei Mot", he: "אחרי מות", ref: "Leviticus 16:1-18:30" },
      { en: "Kedoshim", he: "קדושים", ref: "Leviticus 19:1-20:27" },
      { en: "Emor", he: "אמור", ref: "Leviticus 21:1-24:23" },
      { en: "Behar", he: "בהר", ref: "Leviticus 25:1-26:2" },
      { en: "Bechukotai", he: "בחקתי", ref: "Leviticus 26:3-27:34" },
    ],
  },
  {
    book: "Bamidbar",
    bookHe: "במדבר",
    parshiyot: [
      { en: "Bamidbar", he: "במדבר", ref: "Numbers 1:1-4:20" },
      { en: "Nasso", he: "נשא", ref: "Numbers 4:21-7:89" },
      { en: "Beha'alotcha", he: "בהעלתך", ref: "Numbers 8:1-12:16" },
      { en: "Sh'lach", he: "שלח", ref: "Numbers 13:1-15:41" },
      { en: "Korach", he: "קרח", ref: "Numbers 16:1-18:32" },
      { en: "Chukat", he: "חקת", ref: "Numbers 19:1-22:1" },
      { en: "Balak", he: "בלק", ref: "Numbers 22:2-25:9" },
      { en: "Pinchas", he: "פינחס", ref: "Numbers 25:10-30:1" },
      { en: "Matot", he: "מטות", ref: "Numbers 30:2-32:42" },
      { en: "Masei", he: "מסעי", ref: "Numbers 33:1-36:13" },
    ],
  },
  {
    book: "Devarim",
    bookHe: "דברים",
    parshiyot: [
      { en: "Devarim", he: "דברים", ref: "Deuteronomy 1:1-3:22" },
      { en: "Vaetchanan", he: "ואתחנן", ref: "Deuteronomy 3:23-7:11" },
      { en: "Eikev", he: "עקב", ref: "Deuteronomy 7:12-11:25" },
      { en: "Re'eh", he: "ראה", ref: "Deuteronomy 11:26-16:17" },
      { en: "Shoftim", he: "שופטים", ref: "Deuteronomy 16:18-21:9" },
      { en: "Ki Teitzei", he: "כי תצא", ref: "Deuteronomy 21:10-25:19" },
      { en: "Ki Tavo", he: "כי תבוא", ref: "Deuteronomy 26:1-29:8" },
      { en: "Nitzavim", he: "נצבים", ref: "Deuteronomy 29:9-30:20" },
      { en: "Vayelech", he: "וילך", ref: "Deuteronomy 31:1-31:30" },
      { en: "Ha'azinu", he: "האזינו", ref: "Deuteronomy 32:1-32:52" },
      { en: "Vezot Haberachah", he: "וזאת הברכה", ref: "Deuteronomy 33:1-34:12" },
    ],
  },
];

// Flat lookup by ref (for restoring the picker from a hash route).
export function parashaByRef(ref: string): Parasha | undefined {
  for (const b of PARSHIYOT) {
    const found = b.parshiyot.find((p) => p.ref === ref);
    if (found) return found;
  }
  return undefined;
}
